/**
 * The info panel of a lone selected unit (mockup 02 A): identity (rank, home
 * city, group, hp), its current task and target, the stat grid with rank
 * bonuses in green and the xp bar (`UnitCard`), what is applied (rank, home
 * defence) and what is next (rank by xp, Rank up, Upgrade). The numbers
 * come from `view/unitInfo.ts` and `unitStats`; the buttons send the
 * dock's existing commands (there is no stop order in the sim, so the task
 * line has no buttons). A rival's unit gets no task and no Next rows.
 */
import { useMemo } from 'react';
import { getHexTileMapKey } from '@/game/hexmap/model/keys';
import { unitGlyph } from '@/game/rules/ui';
import type { Unit } from '@/game/sim';
import { type Command, fromMilli, territoryOwners, unitMaxHp, unitRank } from '@/game/sim';
import { chevrons } from '@/game/view/activity';
import { cityName } from '@/game/view/cities';
import { ownGroups } from '@/game/view/groups';
import { labelOrDefault, type Names } from '@/game/view/names';
import {
  type PromotionOption,
  promoteCommands,
  promoteTitle,
  type TrainOption,
  trainCommands,
  trainTitle,
} from '@/game/view/promotions';
import { stockInUnits } from '@/game/view/resources';
import { tickEngagements, tickHealings, tickSheltered, tickWorkers } from '@/game/view/tickCache';
import { rankNext, taskText, unitApplied, unitTask } from '@/game/view/unitInfo';
import { intentReader } from '@/game/view/workerIntent';
import { useSimFrame } from './app/simFrame';
import { Cost } from './Cost';
import { InfoPanel, NextRow } from './InfoPanel';
import { UnitCard } from './UnitCard';
import { VerbLabel } from './VerbLabel';

export function UnitInfoPanel({
  unit,
  training,
  promotions,
  dispatch,
  footer,
  onClose,
  names,
  nameNode,
}: {
  unit: Unit;
  /** Rank up for this unit (`trainOption`), null when it can't rank. */
  training: TrainOption | null;
  /** Upgrades open to it (`promotionOptions`). */
  promotions: readonly PromotionOption[];
  dispatch: (c: Command) => void;
  /** Spy, heal, automate, found-city controls and the hints for them. */
  footer: React.ReactNode;
  onClose: () => void;
  /** The player's names for units, groups and cities (`ui.names`). */
  names?: Names;
  /** The header's name: a custom name or its rename input (the dock owns renaming). */
  nameNode?: React.ReactNode;
}) {
  const frame = useSimFrame();
  const { view: state, rules, ctx, playerId, cityNumbers, fog, terrain } = frame;
  const own = unit.owner === playerId;
  const def = rules.units[unit.type];
  const rank = unitRank(rules, unit);
  const task = useMemo(
    () =>
      unitTask({
        intent: intentReader({
          state,
          rules,
          terrain,
          playerId,
          cityNumbers,
          index: tickWorkers(state, rules),
        })(unit),
        state,
        ctx,
        viewer: playerId,
        unit,
        seen: (cell) => fog.visible.has(getHexTileMapKey(cell)),
        engaged: tickEngagements(state, ctx),
        heals: tickHealings(state, rules),
      }),
    [state, ctx, playerId, unit, fog, rules, terrain, cityNumbers]
  );
  const owners = useMemo(() => territoryOwners(state, rules), [state, rules]);
  const inOwnTerritory =
    state.buildings[owners.get(getHexTileMapKey(unit.pos)) ?? '']?.owner === unit.owner;
  const stored = unit.inside ? state.buildings[unit.inside] : undefined;
  const home = unit.homeCityId ? state.buildings[unit.homeCityId] : undefined;
  const group = ownGroups(state, playerId).find((g) => g.units.some((u) => u.id === unit.id));
  const sheltered = tickSheltered(state, ctx).has(unit.id);
  const hp = {
    hp: Math.ceil(fromMilli(unit.hp)),
    max: Math.ceil(fromMilli(unitMaxHp(rules, unit))),
  };
  const nextRank = own ? rankNext(rules, unit) : null;
  const stock = stockInUnits(state, playerId);

  return (
    <InfoPanel
      kind="unit"
      badge={unitGlyph(rules, unit.type)}
      badgeMark={chevrons(rank)}
      name={def?.name ?? unit.type}
      nameNode={nameNode}
      level={
        rank > 0 ? (
          <span title={`Rank ${rank}`} className="chip shrink-0 text-warn">
            Rank {rank} · {chevrons(rank)}
          </span>
        ) : undefined
      }
      chips={
        <>
          <span className="chip shrink-0 tabular-nums">
            {hp.hp}/{hp.max} hp
          </span>
          {own && home && (
            <span className="chip shrink-0">
              {cityName(home, playerId, cityNumbers, frame.playerName, names)}
            </span>
          )}
          {group && (
            <span className="chip shrink-0">
              {labelOrDefault(names, group.id, `Group ${group.number}`)}
            </span>
          )}
          {own && stored && (
            <span className="chip shrink-0">
              in {(rules.buildings[stored.type]?.name ?? stored.type).toLowerCase()}
            </span>
          )}
          {sheltered && (
            <span
              title="Sheltered: in your completed city's territory it can't be hit unless it attacks"
              className="chip shrink-0 text-ok"
            >
              sheltered
            </span>
          )}
        </>
      }
      hp={hp}
      task={task ? { tone: task.tone, text: taskText(task) } : null}
      stats={<UnitCard rules={rules} type={unit.type} unit={unit} compact hideHeader />}
      applied={unitApplied(rules, unit, own && inOwnTerritory)}
      next={
        own ? (
          <>
            {(nextRank || training) && (
              <NextRow
                glyph="★"
                title={`Rank ${nextRank?.rank ?? rank + 1}`}
                note={
                  training && training.ready.length === 0
                    ? training.reason
                    : (nextRank?.gain ?? null)
                }
                noteTone={training && training.ready.length === 0 ? 'warn' : undefined}
                action={
                  <span className="flex items-center gap-1.5 text-xs tabular-nums text-fg-muted">
                    {nextRank && <span>{nextRank.xpToGo} xp</span>}
                    {nextRank && training && <span className="text-fg-subtle">or</span>}
                    {training && (
                      <button
                        type="button"
                        disabled={training.ready.length === 0}
                        title={trainTitle(rules, training)}
                        onClick={() => {
                          for (const command of trainCommands(training)) dispatch(command);
                        }}
                        className="btn btn-secondary gap-1.5 text-xs tabular-nums"
                      >
                        <VerbLabel verb="rankUp" />
                        <Cost cost={training.cost} stock={stock} />
                      </button>
                    )}
                  </span>
                }
              />
            )}
            {promotions.map((option) => (
              <NextRow
                key={option.to}
                glyph="⬆"
                title={rules.units[option.to]?.name ?? option.to}
                note={option.ready.length === 0 ? option.reason : null}
                noteTone="warn"
                action={
                  <button
                    type="button"
                    disabled={option.ready.length === 0}
                    title={promoteTitle(rules, option)}
                    onClick={() => {
                      for (const command of promoteCommands(option)) dispatch(command);
                    }}
                    className="btn btn-secondary gap-1.5 text-xs tabular-nums"
                  >
                    <VerbLabel verb="upgrade" />
                    <Cost cost={option.cost} stock={stock} />
                  </button>
                }
              />
            ))}
            {!nextRank && !training && promotions.length === 0 && (
              <p className="text-fg-subtle">
                {def?.military ? 'Top rank' : 'Nothing to add to this unit'}
              </p>
            )}
          </>
        ) : null
      }
      footer={footer}
      onClose={onClose}
    />
  );
}
